import { m as motion } from 'framer-motion';
import { GetStaticProps, InferGetStaticPropsType } from 'next';
import { useRef } from 'react';
import type { Thing } from 'schema-dts';
import { PAGES } from '@/consts';
import { getCommonPageProps } from '@/shared/api';
import { getEmployees } from '@/shared/api/employees';
import { getPageDetail } from '@/shared/api/page-detail';
import { getSiteSettings } from '@/shared/api/site-settings';
import DefaultLayout from '@/shared/components/layout/DefaultLayout';
import AdditionalInfoSection from '@/shared/components/sections/AdditionalInfoSection';
import { AdditionalInfo } from '@/shared/components/sections/AdditionalInfoSection/AdditionalInfoSection';
import ServiceHeaderSection from '@/shared/components/sections/ServiceHeaderSection';
import ServicesContent from '@/shared/components/sections/ServicesContent';
import { ServicesContentProps } from '@/shared/components/sections/ServicesContent/ServicesContent';
import JsonLd from '@/shared/components/shared/JsonLd/JsonLd';
import { AppNavigation, CommonPageProps, NonUndefined } from '@/shared/lib/types';
import { generateWebPageJsonLd } from '@/shared/lib/utils/page-json-ld';

const ServicesPage = ({
    h1,
    pageTitle,
    titleDescription,
    bodyClass,
    breadcrumbs,
    meta,
    navigation,
    additionalInfoProps,
    shuttleContentProps,
    buttonText,
    buttonLink,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
    const ref = useRef(null);

    return (
        <DefaultLayout currentPageId={PAGES.SHUTTLES} bodyClass={bodyClass} ref={ref}>
            <h1 className="visually-hidden">{h1}</h1>
            <ServiceHeaderSection
                serviceHeaderSectionProps={{
                    breadcrumbs,
                    h1: pageTitle,
                    titleDescription,
                    navigation,
                    buttonText,
                    buttonLink,
                }}
            />
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1 }}
                className="services-wrapper"
            >
                <ServicesContent servicesContent={shuttleContentProps} />
                <AdditionalInfoSection variant="medium" additionalInfoSectionProps={additionalInfoProps} />
            </motion.div>
            <JsonLd<Thing>
                inBody
                scriptKey="ld-json-shuttles"
                data={generateWebPageJsonLd({
                    name: pageTitle || h1,
                    description: titleDescription || meta.description,
                    pagePath: '/services/shuttles/',
                    breadcrumbs,
                })}
            />
        </DefaultLayout>
    );
};

export default ServicesPage;

type ServicesPageProps = NonUndefined<
    CommonPageProps & {
        h1: string;
        pageTitle: string;
        navigation: AppNavigation;
        titleDescription: string;
        buttonText: string;
        buttonLink: string;
        additionalInfoProps: AdditionalInfo;
        shuttleContentProps: ServicesContentProps;
    }
>;

export const getStaticProps: GetStaticProps<ServicesPageProps> = async () => {
    const pageResponse = await getPageDetail({ params: { slug: 'shuttles' } });

    const [commonPageProps, siteSettings, employeesResponse] = await Promise.all([
        getCommonPageProps(pageResponse.data?.id ?? PAGES.SHUTTLES),
        getSiteSettings({
            params: {
                keys: [
                    'shuttles_first_screen_title',
                    'shuttles_first_screen_desription',
                    'shuttles_first_screen_link',
                    'shuttles_first_screen_link_text',

                    'shuttles_about_title',
                    'shuttles_about_desription',
                    'shuttles_about_document',
                    'shuttles_about_link',
                    'shuttles_about_link_text',

                    'shuttles_contact_title',
                    'shuttles_contact_desription',
                ],
            },
        }),
        getEmployees({ params: { page: pageResponse.data?.id ?? PAGES.SHUTTLES } }),
    ]);

    return {
        props: {
            ...commonPageProps,
            bodyClass: 'services-page',
            meta: {
                ...commonPageProps.meta,
                ...(pageResponse.data?.meta ?? []),
            },
            breadcrumbs: [
                { text: 'TNF', href: '/' },
                { text: 'Сервисы', href: '/services/' },
            ],
            h1: pageResponse.data?.meta?.title || 'Сервисы',
            pageTitle: siteSettings.data?.shuttles_first_screen_title || 'Сервисы',
            titleDescription:
                siteSettings.data?.shuttles_first_screen_desription ||
                'Используйте возможности сервисов, чтобы сделать ваше участие в событиях TNF максимально продуктивным',
            buttonText: siteSettings.data?.shuttles_first_screen_link_text || 'Служба поддержки',
            buttonLink: siteSettings.data?.shuttles_first_screen_link || '',
            navigation: [
                {
                    id: 0,
                    title: 'Бронирование гостиниц',
                    href: '/services/hotels/',
                },
                {
                    id: 1,
                    title: 'Аренда транспорта',
                    href: '/services/car-rent/',
                },
                {
                    id: 2,
                    title: 'Аккредитация транспорта',
                    href: '/services/car-accreditation/',
                },
                {
                    id: 3,
                    title: 'Шаттлы',
                    href: '/services/shuttles/',
                },
                {
                    id: 4,
                    title: 'Зоны питания',
                    href: '/services/food-areas/',
                },
            ],

            shuttleContentProps: {
                title: siteSettings.data?.shuttles_about_title || 'О шаттлах',
                content: siteSettings.data?.shuttles_about_desription || '',
                link: siteSettings.data?.shuttles_about_link || '',
                linkText: siteSettings.data?.shuttles_about_link_text || 'Расписание шаттлов',
                pdfFile: siteSettings.data?.shuttles_about_document?.src
                    ? {
                          src: siteSettings.data.shuttles_about_document.src,
                          title: siteSettings.data.shuttles_about_document.title || 'Расписание курсирования шаттлов',
                          alt: siteSettings.data.shuttles_about_document.alt ?? '',
                      }
                    : null,
            },

            additionalInfoProps: {
                title: siteSettings.data?.shuttles_contact_title || 'Дополнительная информация',
                subtitle:
                    siteSettings.data?.shuttles_contact_desription ||
                    'Получить дополнительную информацию об условиях бронирования можно у оператора Форума в сфере гостеприимства - компании Promo Travel:',
                contacts: employeesResponse.data || [],
            },
        } satisfies ServicesPageProps,
        revalidate: 60,
    };
};
